/**
 * Every hook here talks to whichever agent the surrounding surface belongs to,
 * via `useAgentApi()`. The kind is part of each cache key so the staff chat and
 * the customer panel never share a cached thread list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAgentApi } from '../components/AgentChat/AgentKindProvider'
import { PageContextValue } from '../components/InternalAgent/pageContext'
import { type AnswerFeedback } from '../lib/agent/answerFeedback'
import { CacheKeys } from '../lib/cache'
import {
  ChatItem,
  ChatItemAttachment,
  clearAnswerFeedback,
  createAgentConversation,
  decidePendingAction,
  deleteAgentConversation,
  getActiveTurn,
  getAgentConversation,
  listAgentConversations,
  listChatItems,
  listPendingActions,
  PageContextEntityLabel,
  PageContextPayload,
  rejectAllPendingActions,
  RejectAllPendingActionsResult,
  resolvePageContextEntity,
  saveAnswerFeedback,
  sendAgentConversationMessage,
  SseEvent,
  stopTurn,
  streamTurn,
} from '../models/agentConversation'

const PLACEHOLDER_TITLE = 'New conversation'

export function useAgentConversations(params?: { q?: string; salesLead?: string; organisation?: string }) {
  const { basePath, kind } = useAgentApi()
  const { data, isLoading } = useQuery({
    queryKey: [
      CacheKeys.AGENT_CONVERSATIONS,
      kind,
      params?.q ?? '',
      params?.salesLead ?? '',
      params?.organisation ?? '',
    ],
    queryFn: () => listAgentConversations(basePath, params),
    // While any conversation still has the placeholder title, poll every 30s so
    // the auto-generated title appears without the user having to refresh.
    refetchInterval: (query) => {
      const list = query.state.data ?? []
      return list.some((c) => c.title === PLACEHOLDER_TITLE) ? 30_000 : false
    },
  })
  return { conversations: data ?? [], isLoading }
}

export function useAgentConversation(id: string | undefined) {
  const { basePath, kind } = useAgentApi()
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.AGENT_CONVERSATION, kind, id],
    queryFn: () => getAgentConversation(basePath, id!),
    enabled: Boolean(id),
  })
  return { conversation: data, isLoading }
}

export function useCreateAgentConversation() {
  const queryClient = useQueryClient()
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: (params?: { title?: string; salesLead?: string; organisation?: string }) =>
      createAgentConversation(basePath, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.AGENT_CONVERSATIONS] })
    },
  })
}

export function useDeleteAgentConversation() {
  const queryClient = useQueryClient()
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: (id: string) => deleteAgentConversation(basePath, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.AGENT_CONVERSATIONS] })
    },
  })
}

export function useChatItems(conversationId: string | undefined) {
  const { basePath, kind } = useAgentApi()
  const { data, isLoading, refetch } = useQuery({
    queryKey: [CacheKeys.AGENT_CONVERSATION, kind, conversationId, 'items'],
    queryFn: () => listChatItems(basePath, conversationId!),
    enabled: Boolean(conversationId),
  })
  return { items: data ?? [], isLoading, refetch }
}

/** Save or clear the staff member's rating of one answer, and show it in the cached thread. */
export function useAnswerFeedback(conversationId: string | undefined, chatItemId: string) {
  const { kind } = useAgentApi()
  const queryClient = useQueryClient()
  const showFeedback = (feedback: AnswerFeedback | null) =>
    queryClient.setQueryData<ChatItem[]>(
      [CacheKeys.AGENT_CONVERSATION, kind, conversationId, 'items'],
      (items) => items?.map((item) => (item.id === chatItemId ? { ...item, feedback } : item))
    )
  const save = useMutation({
    mutationFn: (feedback: AnswerFeedback) => saveAnswerFeedback(chatItemId, feedback),
    onSuccess: showFeedback,
  })
  const clear = useMutation({
    mutationFn: () => clearAnswerFeedback(chatItemId),
    onSuccess: () => showFeedback(null),
  })
  return { save, clear }
}

/**
 * Rehydrates pending confirmation actions from the server. The
 * in-memory pending queue in ChatPanel would otherwise be wiped on
 * navigation away/back, even though the PendingAgentAction rows are
 * still PENDING in the DB.
 */
export function usePendingActions(conversationId: string | undefined) {
  const { basePath, kind } = useAgentApi()
  const { data, refetch } = useQuery({
    queryKey: [CacheKeys.AGENT_CONVERSATION, kind, conversationId, 'pending'],
    queryFn: () => listPendingActions(basePath, conversationId!),
    enabled: Boolean(conversationId),
  })
  return { pendingActions: data ?? [], refetch }
}

interface SendMessageVars {
  conversationId: string
  text: string
  documentIds?: string[]
  /** Local attachment metadata, kept only for optimistic rendering. Not sent to the server. */
  attachments?: ChatItemAttachment[]
  /**
   * Frontend-generated UUID echoed back as the persisted ChatItem.client_id.
   * Used to dedupe the optimistic bubble against the persisted version via
   * uniqBy(items+pending, item => item.clientId || item.id).
   */
  clientId: string
  /** The page the user was on when they hit send; injected into this turn only. */
  pageContext?: PageContextPayload | null
  onEvent: (event: SseEvent) => void
  abortSignal?: AbortSignal
  /** Fires once the worker has the turn, so Stop knows what to stop. */
  onTurnStarted?: (turnId: string) => void
}

/**
 * Submit once and follow the accepted turn, reconnecting reads from its cursor. The `variables` returned by
 * useMutation give us the just-sent text + clientId + conversationId for
 * optimistic UI — render the user's message immediately while `isPending`
 * is true, then drop it once items refetches the persisted version (matched
 * by clientId).
 */
export function useSendAgentMessage() {
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: async ({
      conversationId,
      text,
      documentIds,
      clientId,
      pageContext,
      onEvent,
      abortSignal,
      onTurnStarted,
    }: SendMessageVars) => {
      const turnId = await sendAgentConversationMessage(
        basePath,
        conversationId,
        text,
        documentIds ?? [],
        clientId,
        pageContext
      )
      onTurnStarted?.(turnId)
      await streamTurn(basePath, conversationId, turnId, onEvent, { abortSignal })
    },
  })
}

interface ResumeVars {
  conversationId: string
  onEvent: (event: SseEvent) => void
  abortSignal?: AbortSignal
  onTurnStarted?: (turnId: string) => void
}

/** Reattach to whatever is in flight, replaying it from the start. */
export function useResumeActiveTurn() {
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: async ({ conversationId, onEvent, abortSignal, onTurnStarted }: ResumeVars) => {
      const turnId = await getActiveTurn(basePath, conversationId)
      if (!turnId) return null
      onTurnStarted?.(turnId)
      await streamTurn(basePath, conversationId, turnId, onEvent, { abortSignal })
      return turnId
    },
  })
}

export function useStopTurn() {
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: ({ conversationId, turnId }: { conversationId: string; turnId: string }) =>
      stopTurn(basePath, conversationId, turnId),
  })
}

interface DecisionVars {
  conversationId: string
  actionToken: string
  decision: 'confirm' | 'reject'
  onEvent: (event: SseEvent) => void
  onTurnStarted?: (turnId: string) => void
}

export function useDecidePendingAction() {
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: async ({ conversationId, actionToken, decision, onEvent, onTurnStarted }: DecisionVars) => {
      const turnId = await decidePendingAction(basePath, conversationId, actionToken, decision)
      // Null until the last sibling of the group is decided; there is nothing
      // to follow until the backend actually queues the resumed turn.
      if (!turnId) return
      onTurnStarted?.(turnId)
      await streamTurn(basePath, conversationId, turnId, onEvent)
    },
  })
}

interface RejectAllVars {
  conversationId: string
  onEvent: (event: SseEvent) => void
  onTurnStarted?: (turnId: string) => void
  /** Fires as soon as the rows are rejected, before the acknowledgment streams. */
  onRejected?: (result: RejectAllPendingActionsResult) => void
}

/** Reject every pending action, then follow the acknowledgment turn's stream. */
export function useRejectAllPendingActions() {
  const { basePath } = useAgentApi()
  return useMutation({
    mutationFn: async ({ conversationId, onEvent, onTurnStarted, onRejected }: RejectAllVars) => {
      const result = await rejectAllPendingActions(basePath, conversationId)
      onRejected?.(result)
      if (result.turnId) {
        onTurnStarted?.(result.turnId)
        await streamTurn(basePath, conversationId, result.turnId, onEvent)
      }
      return result
    },
  })
}

/**
 * Name the record behind the current page, for the composer's context chip.
 * Pages that carry no record resolve instantly from the route map's own label,
 * so only entity pages hit the network.
 */
export function usePageContextLabel(context: PageContextValue | null) {
  const { basePath, kind } = useAgentApi()
  const entityType = context?.entityType
  const entityId = context?.entityId
  const { data, isLoading } = useQuery<PageContextEntityLabel>({
    queryKey: [CacheKeys.AGENT_PAGE_CONTEXT, kind, entityType ?? '', entityId ?? ''],
    queryFn: () => resolvePageContextEntity(basePath, entityType!, entityId!),
    enabled: Boolean(entityType && entityId),
    // A record's name doesn't move while staff sit on its page.
    staleTime: 5 * 60_000,
    retry: false,
  })
  return { entity: data ?? null, isLoading: Boolean(entityType && entityId) && isLoading }
}
